import React, { useMemo, useRef } from "react"

import { useEvent } from "shared/lib/useEvent"
import { useWindowFocus } from "shared/lib/useWindowFocus"

import { useProjectCollaborationWebsocket } from "../api/useProjectCollaborationWebsocket"
import { parseProjectCollaborationEvent } from "../lib/parseProjectCollaborationEvent"
import { ProjectCollaborationContext } from "../types/ProjectCollaborationContext"
import { ProjectCollaborationMessage } from "../types/ProjectCollaborationEvent"

interface ProjectCollaborationContextProviderProps {
  children: React.ReactNode | React.ReactNode[]
}

interface ProjectCollaborationSubscription {
  subscriptionID: string
  projectID: string
  onMessage: (message: ProjectCollaborationMessage) => void
}

interface PendingUnsubscription {
  subscriptionID: string
  projectID: string
  timeout: NodeJS.Timeout
}

export const ProjectCollaborationContextProvider = React.memo(
  ({ children }: ProjectCollaborationContextProviderProps) => {
    const subscriptionsRef = useRef<ProjectCollaborationSubscription[]>([])
    const pendingUnsubscriptionsRef = useRef<PendingUnsubscription[]>([])

    const notifySubscribers = useEvent((event: MessageEvent) => {
      const messageData = event.data ? JSON.parse(event.data) : null
      const message = parseProjectCollaborationEvent(messageData)
      if (!message) {
        return
      }
      subscriptionsRef.current.forEach(sub => {
        if (sub.projectID === message.project.id) {
          sub.onMessage(message)
        }
      })
    })

    const handleConnectionOpen = useEvent(() => {
      // Re-subscribe to all unique projects when connection opens
      const uniqueProjectIDs = Array.from(
        new Set(subscriptionsRef.current.map(sub => sub.projectID))
      )
      uniqueProjectIDs.forEach(projectID => {
        subscribe(projectID)
      })
    })

    const { connectionState, connect, disconnect, subscribe, unsubscribe } =
      useProjectCollaborationWebsocket({
        onMessage: notifySubscribers,
        onOpen: handleConnectionOpen
      })

    const cleanupSubscription = useEvent(
      (subscriptionID: string, projectID: string) => {
        // Remove subscription from ref

        const updatedSubscriptions = subscriptionsRef.current.filter(
          sub => sub.subscriptionID !== subscriptionID
        )
        subscriptionsRef.current = updatedSubscriptions

        // Only unsubscribe from WebSocket if no other subscriptions exist for this project
        const hasOtherSubscriptionsForProject = updatedSubscriptions.some(
          sub => sub.projectID === projectID
        )
        if (!hasOtherSubscriptionsForProject) {
          unsubscribe(projectID)
        }

        // Remove from pending unsubscriptions using ref
        pendingUnsubscriptionsRef.current =
          pendingUnsubscriptionsRef.current.filter(
            unsubs => unsubs.subscriptionID !== subscriptionID
          )
      }
    )

    const handleUnsubscribe = useEvent(
      (subscriptionID: string, projectID: string) => {
        // Check if this is the last subscription for this project
        const otherSubscriptionsForProject = subscriptionsRef.current.filter(
          sub =>
            sub.projectID === projectID && sub.subscriptionID !== subscriptionID
        )

        // Only schedule unsubscription if this is the last subscription for this project
        if (otherSubscriptionsForProject.length === 0) {
          // Add to pending unsubscriptions using ref
          const timeout = setTimeout(() => {
            cleanupSubscription(subscriptionID, projectID)
          }, 5000)

          pendingUnsubscriptionsRef.current.push({
            subscriptionID,
            projectID,
            timeout
          })
        } else {
          // Just remove the subscription immediately since others exist for this project
          subscriptionsRef.current = subscriptionsRef.current.filter(
            sub => sub.subscriptionID !== subscriptionID
          )
        }
      }
    )

    const handleSubscribe = useEvent(
      (
        projectID: string,
        subscriptionID: string,
        options: { onMessage: (message: ProjectCollaborationMessage) => void }
      ) => {
        const { onMessage } = options || {}

        // Check if there's a pending unsubscription for this project and cancel it
        const pendingUnsubscription = pendingUnsubscriptionsRef.current.find(
          unsubs => unsubs.projectID === projectID
        )

        if (pendingUnsubscription) {
          clearTimeout(pendingUnsubscription.timeout)
          pendingUnsubscriptionsRef.current =
            pendingUnsubscriptionsRef.current.filter(
              unsubs => unsubs.projectID !== projectID
            )
        }
        if (
          !subscriptionsRef.current.find(
            sub => sub.subscriptionID === subscriptionID
          )
        ) {
          subscriptionsRef.current.push({
            subscriptionID,
            projectID,
            onMessage
          })
          subscribe(projectID)
        }

        return () => {
          handleUnsubscribe(subscriptionID, projectID)
        }
      }
    )

    const contextValue = useMemo(
      () => ({
        subscribe: handleSubscribe,
        connectionState
      }),
      [handleSubscribe, connectionState]
    )

    useWindowFocus({
      onIdle: disconnect,
      onResume: connect
    })

    return (
      <ProjectCollaborationContext.Provider value={contextValue}>
        {children}
      </ProjectCollaborationContext.Provider>
    )
  }
)
